/**
 * Admin SPA エントリーポイント。
 *
 * 起動フロー：
 * 1. LIFF 初期化（liffId は本ファイル先頭の LIFF_ID 定数。Task 15 で実値に置換）
 * 2. 未ログインなら liff.login() でリダイレクト
 * 3. profile.userId を取得して store に保存
 * 4. /api/admin/me でオーナー権限を確認（403 なら権限エラー画面）
 * 5. OK なら一覧画面へ
 *
 * 認証情報・キーは一切コードに含めない（LIFF SDK が IDToken を自動取得）。
 */

import { checkOwner, ApiError } from "./api";
import { setRoot, showError } from "./render";
import { state } from "./store";
import { renderList } from "./views/list";

declare global {
  interface Window { liff: any }
}

// Task 15 で本物の Admin LIFF ID に置換する。
// それまではこのプレースホルダのまま。grep しやすいよう一意な文字列にする。
const LIFF_ID = "2009825101-eOm8EqDi";

async function init(): Promise<void> {
  setRoot(`<div class="text-center mt-12 text-gray-500">起動中...</div>`);

  if (LIFF_ID === "<TODO_ADMIN_LIFF_ID>") {
    setRoot(`
      <div class="mt-12 px-4 text-center">
        <div class="text-red-600 font-semibold mb-2">LIFF ID 未設定</div>
        <div class="text-sm text-gray-600">
          admin/src/main.ts の <code>LIFF_ID</code> を本物の Admin LIFF ID に置き換えてください。
        </div>
      </div>
    `);
    return;
  }

  try {
    await window.liff.init({ liffId: LIFF_ID });
  } catch (e: any) {
    showError(`LIFF 初期化エラー：${e?.message ?? e}`);
    return;
  }

  if (!window.liff.isLoggedIn()) {
    window.liff.login();
    return;
  }

  // LIFF IDToken は約1時間で期限切れ。期限切れなら logout + login で更新する。
  // 30秒のバッファを取って、もうすぐ切れるトークンも更新対象にする。
  if (isIdTokenExpired()) {
    refreshLogin();
    return;
  }

  // userId は profile から取得（IDToken 検証は Worker 側で実施）
  try {
    const profile = await window.liff.getProfile();
    state.liffUserId = profile.userId ?? null;
  } catch {
    // プロフィール取得失敗は致命的でないので続行
    state.liffUserId = null;
  }

  // オーナー権限確認
  try {
    await checkOwner();
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      // Token がサーバ側で expired 判定 → 再ログインで更新
      refreshLogin();
      return;
    }
    if (e instanceof ApiError && e.status === 403) {
      renderForbidden();
      return;
    }
    showError(`認証エラー：${(e as any)?.message ?? e}`);
    renderAuthError();
    return;
  }

  await renderList();
}

/**
 * LIFF IDToken の有効期限を確認する。
 * - getDecodedIDToken() が null/undefined → 取得不可とみなして expired 扱い
 * - exp（UNIX秒）が 30秒先より過去なら expired
 */
function isIdTokenExpired(): boolean {
  try {
    const decoded = window.liff.getDecodedIDToken();
    if (!decoded || typeof decoded.exp !== "number") return true;
    const nowSec = Date.now() / 1000;
    return decoded.exp < nowSec + 30;
  } catch {
    return true;
  }
}

/**
 * トークン期限切れ時に LINE で再ログインしてフレッシュなトークンを取得する。
 * logout で内部キャッシュを破棄してから login すると確実に新トークンが発行される。
 */
function refreshLogin(): void {
  try {
    window.liff.logout();
  } catch {
    // ignore
  }
  window.liff.login({ redirectUri: location.href });
}

function renderForbidden(): void {
  setRoot(`
    <div class="mt-16 px-6 text-center">
      <div class="text-2xl font-bold text-gray-900 mb-3">権限がありません</div>
      <div class="text-gray-700 leading-relaxed">
        この管理画面はオーナー専用です。<br>
        ご自身の LINE アカウントで間違いないかご確認ください。
      </div>
      <div class="text-xs text-gray-400 mt-6">
        ${state.liffUserId ? `userId: ${state.liffUserId.slice(0, 6)}...` : ""}
      </div>
    </div>
  `);
}

function renderAuthError(): void {
  setRoot(`
    <div class="mt-16 px-6 text-center">
      <div class="text-xl font-bold text-gray-900 mb-3">認証に失敗しました</div>
      <div class="text-gray-700 leading-relaxed">
        通信状況を確認して、画面を再読み込みしてください。
      </div>
      <button
        id="btn-reload"
        class="mt-6 tap-target px-6 py-3 bg-brand-green text-white rounded-lg">
        再読み込み
      </button>
    </div>
  `);
  document.getElementById("btn-reload")?.addEventListener("click", () => {
    location.reload();
  });
}

document.addEventListener("DOMContentLoaded", init);
